import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Loader2, DollarSign, TrendingUp, TrendingDown, Target, User, BarChart } from 'lucide-react';

const STAGE_COLORS = {
    DISCOVERY: 'bg-blue-400',
    QUALIFIED: 'bg-purple-400',
    PROPOSAL: 'bg-yellow-400',
    NEGOTIATION: 'bg-orange-400',
    CLOSED: 'bg-gray-400'
};

export default function RevenueDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('30d');



    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/sales-reporting/pipeline-trends?period=${period}`);
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load pipeline trends.');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadData();
    }, [loadData, period]);

    if (error) return <div className="p-8 text-red-600 bg-red-50 rounded-xl m-8">{error}</div>;

    const renderTrend = (trendData) => {
        if (!trendData || trendData.percentageChange === null) return <span className="text-gray-400 text-xs mt-1 block">Static / No prior period</span>;

        let color = 'text-gray-500';
        let arrow = '';
        if (trendData.trendDirection === 'UP') {
            color = 'text-green-600';
            arrow = '↑';
        } else if (trendData.trendDirection === 'DOWN') {
            color = 'text-red-500';
            arrow = '↓';
        }

        return (
            <span className={`text-xs mt-1 block font-medium ${color}`}>
                {arrow} {Math.abs(trendData.percentageChange)}% vs previous period
            </span>
        );
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
    };

    return (
        <div className="space-y-6">

            {/* Header / Picker */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2 text-gray-900">
                        <BarChart className="w-8 h-8 text-indigo-600" /> Revenue & Pipeline
                    </h1>
                    <p className="text-gray-500 mt-1">Track pipeline generation, won revenue, and sales cycle efficiency.</p>
                </div>

                <div className="flex items-center gap-2 bg-white p-1 rounded-xl shadow-sm border">
                    <span className="text-sm font-semibold text-gray-500 px-3">Timeframe</span>
                    <select
                        className="border-none bg-gray-50 rounded-lg p-2 text-sm font-medium focus:ring-0 outline-none w-40 cursor-pointer hover:bg-gray-100 transition-colors"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                    >
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="90d">Last 90 Days</option>
                        <option value="all">All Time</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
            ) : data && (
                <>
                    {/* Top Stat Cards */}
                    <div className="grid grid-cols-3 gap-6">
                        <div className="bg-white p-5 rounded-xl border shadow-sm">
                            <div className="flex items-center gap-2 text-gray-500 mb-2">
                                <TrendingUp className="w-4 h-4 text-indigo-500" /> <h3 className="text-sm font-semibold uppercase tracking-wider">Open Pipeline Generated</h3>
                            </div>
                            <div className="text-3xl font-bold text-gray-900">{formatCurrency(data.trends.openPipelineValue.current)}</div>
                            <div className="flex justify-between items-center mt-1">
                                {renderTrend(data.trends.openPipelineValue)}
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl border shadow-sm border-green-200">
                            <div className="flex items-center gap-2 text-green-700 mb-2">
                                <DollarSign className="w-4 h-4" /> <h3 className="text-sm font-semibold uppercase tracking-wider">Closed Won</h3>
                            </div>
                            <div className="text-3xl font-bold text-gray-900">{formatCurrency(data.trends.wonValue.current)}</div>
                            <div className="flex justify-between items-center mt-1">
                                {renderTrend(data.trends.wonValue)}
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl border shadow-sm border-red-200">
                            <div className="flex items-center gap-2 text-red-700 mb-2">
                                <TrendingDown className="w-4 h-4" /> <h3 className="text-sm font-semibold uppercase tracking-wider">Closed Lost</h3>
                            </div>
                            <div className="text-3xl font-bold text-gray-900">{formatCurrency(data.trends.lostValue.current)}</div>
                            <div className="flex justify-between items-center mt-1">
                                {renderTrend({ ...data.trends.lostValue, percentageChange: data.trends.lostValue.percentageChange !== null ? -data.trends.lostValue.percentageChange : null })}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-6">

                        {/* Stage Funnel */}
                        <div className="col-span-1 bg-white border rounded-xl shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4 border-b pb-2">
                                <Target className="w-4 h-4 text-gray-500" />
                                <h3 className="text-sm font-bold text-gray-900">Stage Distribution (Open)</h3>
                            </div>

                            {Object.keys(data.stageBreakdown).length === 0 ? (
                                <div className="text-sm text-gray-500 text-center py-6">No open opportunities originated in this timeframe.</div>
                            ) : (
                                <div className="space-y-4">
                                    {Object.entries(data.stageBreakdown).sort((a, b) => b[1].value - a[1].value).map(([stage, stats]) => (
                                        <div key={stage}>
                                            <div className="flex justify-between text-xs font-semibold mb-1">
                                                <span className="text-gray-700">{stage} <span className="text-gray-400 font-normal">({stats.count} opps)</span></span>
                                                <span className="text-gray-900">{formatCurrency(stats.value)}</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${STAGE_COLORS[stage] || 'bg-blue-500'}`}
                                                    style={{ width: `${Math.max(10, (stats.value / data.trends.openPipelineValue.current) * 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Owner Breakdown */}
                        <div className="col-span-2 bg-white border rounded-xl shadow-sm overflow-hidden">
                            <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500" />
                                <h3 className="font-bold text-gray-900">Owner Performance (Current Period)</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-white border-b text-gray-500">
                                        <tr>
                                            <th className="p-3 font-medium">Owner</th>
                                            <th className="p-3 font-medium text-right">Open Pipeline</th>
                                            <th className="p-3 font-medium text-center">Won (Count)</th>
                                            <th className="p-3 font-medium text-right">Won Value</th>
                                            <th className="p-3 font-medium text-center">Lost (Count)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {data.owners.length === 0 ? (
                                            <tr><td colSpan="5" className="text-center p-4 text-gray-500">No owner activity in this timeframe.</td></tr>
                                        ) : data.owners.sort((a, b) => b.wonValue - a.wonValue).map((owner, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="p-3 font-medium text-gray-900">
                                                    <div className="font-semibold">{owner.owner.name}</div>
                                                    <div className="text-xs text-gray-500 font-normal">{owner.owner.email || 'Unassigned'}</div>
                                                </td>
                                                <td className="p-3 text-right font-medium text-indigo-600">
                                                    {formatCurrency(owner.openValue)} <span className="text-xs text-gray-400 block font-normal">{owner.openCount} opps</span>
                                                </td>
                                                <td className="p-3 text-center text-gray-600 font-bold">
                                                    <span className={`${owner.wonCount > 0 ? 'text-green-600 bg-green-50 px-2 py-0.5 rounded' : ''}`}>{owner.wonCount}</span>
                                                </td>
                                                <td className="p-3 text-right font-bold text-green-700">
                                                    {formatCurrency(owner.wonValue)}
                                                </td>
                                                <td className="p-3 text-center text-gray-500">
                                                    {owner.lostCount}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
